type GeoJSONAuthStrategy = 'none' | 'bearer_token' | 'header_api_key' | string;

export interface GeoJSONSourceDiagnosticsInput {
  source_kind?: string | null;
  metadata?: Record<string, unknown> | null;
  vector_layers?: Array<Record<string, unknown> | string> | null;
  auth_strategy?: GeoJSONAuthStrategy | null;
  auth_metadata?: {
    header_name?: string | null;
  } | null;
  request_recipe?: Record<string, unknown> | null;
}

export type GeoJSONValidationState = 'pending' | 'ready' | 'error';

export interface GeoJSONSourceDiagnostics {
  state: GeoJSONValidationState;
  statusLabel: string;
  detail: string;
  guidance: string | null;
  rawError: string | null;
  lastFetchStatus: number | null;
  lastFetchedAt: string | null;
  lastSuccessfulFetchAt: string | null;
}

type GeoJSONFetchIssue = {
  label: string;
  guidance: string;
};

const isGeoJSONSource = (source?: Pick<GeoJSONSourceDiagnosticsInput, 'source_kind'> | null) =>
  source?.source_kind === 'geojson_http';

const readMetadataString = (metadata: Record<string, unknown> | null | undefined, key: string): string | null => {
  const value = metadata?.[key];
  return typeof value === 'string' && value.trim() ? value.trim() : null;
};

const readMetadataNumber = (metadata: Record<string, unknown> | null | undefined, key: string): number | null => {
  const value = metadata?.[key];
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return null;
};

const classifyFetchIssue = (source?: GeoJSONSourceDiagnosticsInput | null): GeoJSONFetchIssue => {
  const metadata = source?.metadata;
  const rawError = readMetadataString(metadata, 'last_fetch_error');
  const errorText = rawError?.toLowerCase() ?? '';
  const status = readMetadataNumber(metadata, 'last_fetch_status');
  const authStrategy = source?.auth_strategy ?? 'none';
  const headerName = source?.auth_metadata?.header_name?.trim();
  const authHint =
    authStrategy === 'bearer_token'
      ? 'Check the stored bearer token and confirm the upstream API accepts it for this endpoint.'
      : authStrategy === 'header_api_key'
        ? `Check the ${headerName || 'configured'} header name and API key value against the upstream API requirements.`
        : 'Check whether the upstream API expects authentication or tenant-scoped access that has not been configured here.';

  if (
    errorText.includes('failed to resolve live geojson credentials') ||
    errorText.includes('openbao') ||
    errorText.includes('secret reference') ||
    errorText.includes('secret path') ||
    errorText.includes('stored credential')
  ) {
    return {
      label: 'Stored credentials unavailable',
      guidance:
        'Check that the saved secret reference still exists in OpenBao for this source. If the credential was rotated or deleted, save the auth settings again and retry fetch.',
    };
  }

  if (status === 401 || errorText.includes('unauthorized') || errorText.includes('bearer') || errorText.includes('api key')) {
    return {
      label: 'Authentication failed',
      guidance: authHint,
    };
  }

  if (status === 403 || errorText.includes('forbidden') || errorText.includes('missing in openbao')) {
    return {
      label: 'Upstream access denied',
      guidance: `${authHint} If the secret is stored externally, verify the configured secret reference still exists.`,
    };
  }

  if (status === 404 || errorText.includes('not found')) {
    return {
      label: 'Upstream URL not found',
      guidance: source?.request_recipe
        ? 'Verify the request recipe URL/path and any templated query parameters. The upstream endpoint is not being found.'
        : 'Verify the saved GeoJSON URL path and confirm the upstream endpoint still exists.',
    };
  }

  if (status === 429 || errorText.includes('too many requests') || errorText.includes('rate limit')) {
    return {
      label: 'Rate limited',
      guidance: 'Check the upstream API quota or rate limit policy before retrying validation again.',
    };
  }

  if (
    errorText.includes('not valid geojson') ||
    errorText.includes('invalid geojson') ||
    errorText.includes('featurecollection') ||
    errorText.includes('application/geo+json') ||
    errorText.includes('application/json')
  ) {
    return {
      label: 'Invalid GeoJSON payload',
      guidance: 'Confirm the upstream returns a valid GeoJSON FeatureCollection for this request, not HTML, plain JSON, NDJSON, or another envelope.',
    };
  }

  if (
    errorText.includes('timeout') ||
    errorText.includes('deadline exceeded') ||
    errorText.includes('tls') ||
    errorText.includes('certificate') ||
    errorText.includes('x509') ||
    errorText.includes('no such host') ||
    errorText.includes('dial tcp') ||
    errorText.includes('connection refused')
  ) {
    return {
      label: 'Network or TLS error',
      guidance: 'Verify the upstream host is reachable from the mapping proxy and that its DNS and TLS configuration are valid.',
    };
  }

  if (
    errorText.includes('request recipe') ||
    errorText.includes('body_template') ||
    errorText.includes('invalid method') ||
    errorText.includes('marshal') ||
    errorText.includes('unmarshal')
  ) {
    return {
      label: 'Request recipe error',
      guidance: 'Review the advanced request recipe JSON, including method, headers, query defaults, and POST body template fields.',
    };
  }

  if (status != null && status >= 500) {
    return {
      label: 'Upstream unavailable',
      guidance: 'The upstream API returned a server error. Confirm the provider is healthy and reachable before retrying validation.',
    };
  }

  return {
    label: 'Fetch error',
    guidance: 'Review the upstream URL, auth settings, and advanced request recipe. Then retry once the third-party API is reachable.',
  };
};

export const getGeoJSONValidationState = (
  source?: Pick<GeoJSONSourceDiagnosticsInput, 'source_kind' | 'metadata' | 'vector_layers'> | null,
): GeoJSONValidationState => {
  if (!isGeoJSONSource(source)) {
    return 'ready';
  }
  const state = readMetadataString(source.metadata, 'validation_state')?.toLowerCase() ?? '';
  if (state === 'pending' || state === 'ready' || state === 'error') {
    return state;
  }
  if (readMetadataString(source.metadata, 'last_successful_fetch_at')) {
    return 'ready';
  }
  if (Array.isArray(source.vector_layers) && source.vector_layers.length > 0) {
    return 'ready';
  }
  return 'pending';
};

export const getGeoJSONSourceDiagnostics = (
  source?: GeoJSONSourceDiagnosticsInput | null,
): GeoJSONSourceDiagnostics => {
  const metadata = source?.metadata;
  const state = getGeoJSONValidationState(source);
  const rawError = readMetadataString(metadata, 'last_fetch_error');
  const lastFetchStatus = readMetadataNumber(metadata, 'last_fetch_status');
  const lastFetchedAt = readMetadataString(metadata, 'last_fetched_at');
  const lastSuccessfulFetchAt = readMetadataString(metadata, 'last_successful_fetch_at');

  if (!isGeoJSONSource(source)) {
    return {
      state: 'ready',
      statusLabel: 'Ready',
      detail: 'Runtime proxy is ready.',
      guidance: null,
      rawError: null,
      lastFetchStatus: null,
      lastFetchedAt: null,
      lastSuccessfulFetchAt: null,
    };
  }

  if (state === 'pending') {
    return {
      state,
      statusLabel: 'Pending validation',
      detail: 'Saved. Validation is running in the background.',
      guidance: 'If validation does not complete, verify the URL, auth settings, and request recipe.',
      rawError,
      lastFetchStatus,
      lastFetchedAt,
      lastSuccessfulFetchAt,
    };
  }

  if (state === 'error') {
    const issue = classifyFetchIssue(source);
    return {
      state,
      statusLabel: issue.label,
      detail: rawError || 'Latest fetch failed.',
      guidance: issue.guidance,
      rawError,
      lastFetchStatus,
      lastFetchedAt,
      lastSuccessfulFetchAt,
    };
  }

  return {
    state,
    statusLabel: 'Ready',
    detail: 'Runtime proxy is ready.',
    guidance: null,
    rawError,
    lastFetchStatus,
    lastFetchedAt,
    lastSuccessfulFetchAt,
  };
};
